const formatCurrency = (value) => {
  const num = Number(value || 0);
  return '\u20B9' + num.toLocaleString('en-IN');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'});
};

const escapeHtml = (str) => {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/\n/g, '<br/>');
};

export const generateBillHtml = (order, tenant, billSettings) => {
  const businessName = order.orgUnitName || tenant?.name || '';
  const businessPhone = order.orgUnitPhone || tenant?.ownerPhone || '';
  const businessAddress = order.orgUnitAddress
    || [tenant?.address, tenant?.city, tenant?.state, tenant?.pincode].filter(Boolean).join(', ');

  // Build items rows — BASE lines only, add-ons as sub-items
  let itemRows = '';
  let itemIndex = 0;
  const lines = order.lines || [];

  lines.forEach((line) => {
    if (line.itemType === 'ADD_ON') return; // skip standalone add-on lines; they appear under their parent
    itemIndex++;
    const lineTotal = Number(line.unitPrice || 0) + Number(line.addOnsTotal || 0);
    itemRows += `
      <tr>
        <td>${itemIndex}</td>
        <td>${escapeHtml(line.catalogItemName)}${line.itemName ? ' - ' + escapeHtml(line.itemName) : ''}</td>
        <td>${formatDate(line.deliveryDate || order.deliveryDate)}</td>
        <td class="right">${formatCurrency(line.unitPrice)}</td>
      </tr>`;

    if (line.addOnLines && line.addOnLines.length > 0) {
      line.addOnLines.forEach((ao) => {
        itemRows += `
          <tr class="addon-row">
            <td></td>
            <td class="addon-name">+ ${escapeHtml(ao.itemName)}</td>
            <td></td>
            <td class="right">${formatCurrency(ao.unitPrice)}</td>
          </tr>`;
      });
    }
  });

  const totalAmount = Number(order.totalAmount || 0);
  const discountAmount = Number(order.discountAmount || 0);
  const taxAmount = Number(order.taxAmount || 0);
  const advanceAmount = Number(order.advanceAmount || 0);
  const balanceAmount = Number(order.balanceAmount || 0);

  const discountRow = discountAmount > 0
    ? `<tr><td>Discount</td><td class="right">- ${formatCurrency(discountAmount)}</td></tr>`
    : '';

  let taxRows = '';
  if (taxAmount > 0 && order.taxBreakdown) {
    try {
      const taxLines = JSON.parse(order.taxBreakdown);
      taxLines.forEach(line => {
        taxRows += `<tr><td>${escapeHtml(line.name)} (${line.percentage}%)</td><td class="right">${formatCurrency(line.amount)}</td></tr>`;
      });
    } catch (_) {
      taxRows = `<tr><td>Tax</td><td class="right">${formatCurrency(taxAmount)}</td></tr>`;
    }
  }

  const termsTitle = billSettings?.termsHeader || 'Terms & Conditions';
  const termsHtml = billSettings?.termsContent
    ? `<div class="section">
        <div class="section-title">${escapeHtml(termsTitle)}</div>
        <div class="terms-text">${escapeHtml(billSettings.termsContent)}</div>
      </div>`
    : '';

  const footerHtml = billSettings?.footerMessage
    ? `<div class="footer-message">${escapeHtml(billSettings.footerMessage)}</div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: -apple-system, sans-serif; font-size: 13px; color: #212121; padding: 16px; background: #fff; }
  .header { text-align: center; margin-bottom: 16px; border-bottom: 2px solid #1B5E20; padding-bottom: 12px; }
  .business-name { font-size: 20px; font-weight: bold; color: #1B5E20; }
  .business-info { font-size: 11px; color: #757575; margin-top: 4px; }
  .meta-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; background: #F5F5F5; padding: 10px; border-radius: 6px; }
  .meta-item { flex: 1; min-width: 45%; }
  .meta-label { font-size: 10px; color: #757575; text-transform: uppercase; }
  .meta-value { font-size: 13px; font-weight: 600; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  th { background: #1B5E20; color: #fff; padding: 8px 6px; text-align: left; font-size: 11px; text-transform: uppercase; }
  th.right, td.right { text-align: right; }
  td { padding: 6px; border-bottom: 1px solid #E0E0E0; font-size: 12px; }
  .addon-row td { border-bottom: none; padding-top: 0; }
  .addon-name { color: #757575; font-size: 11px; padding-left: 8px; }
  .totals-table { width: 60%; margin-left: auto; }
  .totals-table td { padding: 4px 6px; font-size: 12px; }
  .totals-table tr:last-child td { font-weight: bold; font-size: 14px; border-top: 2px solid #1B5E20; color: #D32F2F; }
  .section { margin-bottom: 12px; }
  .section-title { font-size: 11px; font-weight: bold; color: #1B5E20; text-transform: uppercase; margin-bottom: 4px; }
  .terms-text { font-size: 11px; color: #757575; }
  .footer-message { text-align: center; font-size: 12px; color: #757575; font-weight: 600; margin-top: 16px; padding-top: 8px; border-top: 1px solid #E0E0E0; }
  .generated { text-align: center; font-size: 9px; color: #BDBDBD; margin-top: 12px; }
  .powered { text-align: center; font-size: 9px; color: #BDBDBD; margin-top: 2px; }
</style>
</head>
<body>
  <div class="header">
    <div class="business-name">${escapeHtml(businessName)}</div>
    ${businessAddress ? `<div class="business-info">${escapeHtml(businessAddress)}</div>` : ''}
    ${businessPhone ? `<div class="business-info">Phone: ${escapeHtml(businessPhone)}</div>` : ''}
    ${billSettings?.taxName && billSettings?.taxNumber ? `<div class="business-info">${escapeHtml(billSettings.taxName)}: ${escapeHtml(billSettings.taxNumber)}</div>` : ''}
  </div>

  <div class="meta-row">
    <div class="meta-item">
      <div class="meta-label">Bill No</div>
      <div class="meta-value">${escapeHtml(order.orderNumber)}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Date</div>
      <div class="meta-value">${formatDate(order.orderDate)}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Customer</div>
      <div class="meta-value">${escapeHtml(order.customerName)}</div>
    </div>
    <div class="meta-item">
      <div class="meta-label">Phone</div>
      <div class="meta-value">${escapeHtml(order.customerPhone || '-')}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Item</th>
        <th>Exp. Delivery</th>
        <th class="right">Price</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <table class="totals-table">
    <tr><td>Total Amount</td><td class="right">${formatCurrency(totalAmount)}</td></tr>
    ${discountRow}
    ${taxRows}
    <tr><td>Amount Paid</td><td class="right">${formatCurrency(advanceAmount)}</td></tr>
    <tr><td>Balance Due</td><td class="right">${formatCurrency(balanceAmount)}</td></tr>
  </table>

  ${termsHtml}
  ${footerHtml}

  <div class="generated">This is a computer generated bill.</div>
  <div class="powered">Powered by TailorMateApp.com</div>
</body>
</html>`;
};

export const generateBillSummaryText = (order, tenant) => {
  const tenantName = tenant?.name || '';
  const lines = order.lines || [];

  let itemsText = '';
  let idx = 0;
  lines.forEach((line) => {
    if (line.itemType === 'ADD_ON') return;
    idx++;
    const price = Number(line.unitPrice || 0);
    itemsText += `${idx}. ${line.catalogItemName}${line.itemName ? ' - ' + line.itemName : ''}: \u20B9${price.toLocaleString('en-IN')}\n`;
  });

  const totalAmount = Number(order.totalAmount || 0);
  const discountAmount = Number(order.discountAmount || 0);
  const taxAmount = Number(order.taxAmount || 0);
  const advanceAmount = Number(order.advanceAmount || 0);
  const balanceAmount = Number(order.balanceAmount || 0);

  let text = `${tenantName}\n`;
  text += `Bill No: ${order.orderNumber}\n`;
  text += `Date: ${formatDate(order.orderDate)}\n`;
  text += `Customer: ${order.customerName}\n\n`;
  text += `Items:\n${itemsText}\n`;
  text += `Total: \u20B9${totalAmount.toLocaleString('en-IN')}\n`;
  if (discountAmount > 0) {
    text += `Discount: -\u20B9${discountAmount.toLocaleString('en-IN')}\n`;
  }
  if (taxAmount > 0 && order.taxBreakdown) {
    try {
      const taxLines = JSON.parse(order.taxBreakdown);
      taxLines.forEach(line => {
        text += `${line.name} (${line.percentage}%): \u20B9${Number(line.amount).toLocaleString('en-IN')}\n`;
      });
    } catch (_) {
      text += `Tax: \u20B9${taxAmount.toLocaleString('en-IN')}\n`;
    }
  }
  text += `Paid: \u20B9${advanceAmount.toLocaleString('en-IN')}\n`;
  text += `Balance Due: \u20B9${balanceAmount.toLocaleString('en-IN')}\n`;

  return text;
};
